import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { LogOut, Trash2, CheckCircle2, RotateCcw, RefreshCw } from "lucide-react";
import { api, getToken, TOKEN_KEY, formatApiError } from "../lib/api";
import { Logo } from "../components/Logo";

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await api.login(email, password);
      localStorage.setItem(TOKEN_KEY, token);
      onSuccess();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-[#08090B] px-4 py-3 font-inter text-sm text-white placeholder:text-hars-muted focus:border-hars-orange focus:outline-none";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#08090B] px-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#0F1115] p-8" data-testid="admin-login-form">
        <div className="mb-6 flex flex-col items-center gap-3">
          <Logo size={40} />
          <p className="heading text-lg text-white">ADMIN PANELİ</p>
        </div>
        <div className="space-y-4">
          <input className={inputCls} type="email" placeholder="E-posta" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" />
          <input className={inputCls} type="password" placeholder="Şifre" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
          <button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full rounded-full bg-hars-orange px-6 py-3 font-inter text-sm font-semibold text-white disabled:opacity-60">
            {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await api.listContacts());
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Kayıtlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (it) => {
    const status = it.status === "new" ? "handled" : "new";
    try {
      await api.updateContact(it.id, status);
      setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, status } : x)));
    } catch { toast.error("Güncellenemedi"); }
  };

  const remove = async (id) => {
    try {
      await api.deleteContact(id);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Silindi");
    } catch { toast.error("Silinemedi"); }
  };

  const newCount = items.filter((i) => i.status === "new").length;

  return (
    <div className="min-h-screen bg-[#08090B] px-4 py-8 md:px-10">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={32} />
            <span className="rounded-full bg-hars-orange/15 px-3 py-1 font-inter text-xs text-hars-orange">{newCount} yeni</span>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 font-inter text-sm text-white hover:border-hars-orange" data-testid="admin-refresh">
              <RefreshCw size={15} /> Yenile
            </button>
            <button onClick={onLogout} className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 font-inter text-sm text-white hover:border-hars-orange" data-testid="admin-logout">
              <LogOut size={15} /> Çıkış
            </button>
          </div>
        </div>

        <h1 className="heading mb-6 text-3xl text-white">RANDEVU TALEPLERİ</h1>

        {loading ? (
          <p className="font-inter text-hars-muted">Yükleniyor...</p>
        ) : items.length === 0 ? (
          <p className="font-inter text-hars-muted" data-testid="admin-empty">Henüz talep yok.</p>
        ) : (
          <div className="space-y-3" data-testid="admin-list">
            {items.map((it) => (
              <div key={it.id} className={`rounded-2xl border p-5 ${it.status === "new" ? "border-hars-orange/30 bg-[#0F1115]" : "border-white/10 bg-[#0F1115]/50"}`} data-testid={`admin-row-${it.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <p className="heading text-xl text-white">{it.name}</p>
                      {it.status === "new" ? (
                        <span className="rounded-full bg-hars-orange px-2 py-0.5 font-inter text-[10px] font-bold text-white">YENİ</span>
                      ) : (
                        <span className="rounded-full bg-white/10 px-2 py-0.5 font-inter text-[10px] text-hars-muted">İŞLENDİ</span>
                      )}
                    </div>
                    <p className="mt-1 font-inter text-sm text-hars-orange">{it.phone}</p>
                    <p className="mt-2 font-inter text-sm text-white/80">
                      <span className="text-hars-muted">Araç:</span> {it.vehicle || "-"} · <span className="text-hars-muted">Hizmet:</span> {it.service || "-"}
                    </p>
                    {it.note && <p className="mt-1 font-inter text-sm text-hars-muted">"{it.note}"</p>}
                    <p className="mt-2 font-inter text-xs text-hars-muted">{new Date(it.created_at).toLocaleString("tr-TR")}</p>
                  </div>
                  <div className="flex gap-2">
                    <a href={`https://wa.me/${it.phone.replace(/\D/g, "").replace(/^0/, "90")}`} target="_blank" rel="noopener noreferrer" className="rounded-lg border border-white/10 px-3 py-2 text-xs text-white hover:border-hars-orange" data-testid={`admin-wa-${it.id}`}>WhatsApp</a>
                    <button onClick={() => toggle(it)} className="rounded-lg border border-white/10 p-2 text-white hover:border-hars-orange" title="Durum" data-testid={`admin-toggle-${it.id}`}>
                      {it.status === "new" ? <CheckCircle2 size={16} /> : <RotateCcw size={16} />}
                    </button>
                    <button onClick={() => remove(it.id)} className="rounded-lg border border-white/10 p-2 text-red-400 hover:border-red-400" title="Sil" data-testid={`admin-delete-${it.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!getToken());

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  return authed ? <Dashboard onLogout={logout} /> : <Login onSuccess={() => setAuthed(true)} />;
}
